import React, { useState, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { GiCrossedBones } from "react-icons/gi";
import { LuSave } from "react-icons/lu";
import { Mail, Trash2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { RiFileExcel2Line } from "react-icons/ri";
import { FiChevronDown } from "react-icons/fi";
import { Checkbox } from "@chakra-ui/react";
import { AgCheckbox } from "ag-grid-community";
import { useGetGroupsQuery } from "../../../../services/redux/api/components";

// Custom hook for detecting clicks outside
function useOutsideClick(ref, handler) {
  React.useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

// Multi-select inline component with search
function MultiSelectInline({ options = [], value = [], onChange, placeholder = "Select groups..." }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const rootRef = useRef(null);
  useOutsideClick(rootRef, () => setOpen(false));

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fieldBase = "border border-gray-300 rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`${fieldBase} min-h-[40px] py-2 w-full flex items-center gap-2 justify-between bg-white`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 flex-1 flex-wrap max-h-24 overflow-y-auto">
          {value.length === 0 ? (
            <span className="text-gray-400 truncate">{placeholder}</span>
          ) : (
            <div className="flex items-center gap-1 flex-wrap">
              {value.map((id) => {
                const o = options.find((x) => x.id === id);
                return (
                  <span key={id} className="bg-gray-100 px-2 py-0.5 rounded-md text-gray-700 whitespace-nowrap text-sm">
                    {o?.label ?? id}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-gray-500 text-sm">{value.length > 0 ? `${value.length} selected` : ""}</span>
          <FiChevronDown className="text-gray-500" />
        </div>
      </button>

      {open && (
        <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 shadow-lg rounded-md overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-52 overflow-auto p-2">
            {filteredOptions.length === 0 ? (
              <div className="text-gray-400 text-center py-2">No groups found</div>
            ) : (
              filteredOptions.map((o) => (
                <label key={o.id} className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer">
                  <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} className="w-4 h-4" />
                  <span className="">{o.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const PolicyRegister = () => {
    const navigate = useNavigate();
    const [selectedGroups, setSelectedGroups] = useState([]);
    const { data: groupsResult } = useGetGroupsQuery({ pageNumber: 1, pageSize: 1000 });
    
    const groupOptions = (groupsResult?.data || []).map((g) => ({
      id: g.GroupId,
      label: `${g.GroupCode} - ${g.GroupName}`,
    }));

    const [members, setMembers] = useState([
        {
            policyNo: "",
            groupCode: "",
            policyHolder: "",
            expiryDate: "",
            product: "",
            sum: "",
            premium: "",
        }
    ]);

    const validationSchema = Yup.object({
        renewalFrom: Yup.string().required("Renewal period 'From' is required"),
        renewalTo: Yup.string().required("Renewal period 'To' is required"),
        paymentMode: Yup.string().required("Payment mode is required"),
        productFilter: Yup.string().required("Product is required"),
        members: Yup.array()
            .of(
                Yup.object({
                    quotationNo: Yup.string().required("Quotation number is required"),
                    policyHolder: Yup.string().required("Policy holder is required"),
                    expiryDate: Yup.string().required("Expiry date is required"),
                    product: Yup.string().required("Product is required"),
                    sum: Yup.string().required("Sum is required"),
                    renewalPremium: Yup.string().required("Renewal premium is required"),
                })
            )
            .min(1, "At least one member is required"),
    });


    const handleSubmit = (values, { resetForm }) => {
        const formData = {
            ...values,
            members: members,
        };
        console.log("Submitted:", formData);
        alert("Form Submitted!");
        resetForm();
        setMembers([]);
    };

    return (
        <div className="formContainer lg:p-4 rounded" style={{ boxShadow: "0 0 8px #ccc" }}>
            <Formik
                initialValues={{
                    renewalFrom: "",
                    renewalTo: "",
                    paymentMode: "",
                    productFilter: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, isSubmitting, setFieldValue }) => (
                    <Form className="p-4 bg-white rounded">
                        <div className="flex justify-between items-center border-b border-brandRed pb-2 mb-4">
                            {/* Left side - Text */}
                            <div>
                                <h5 className="text-brandRed font-bold">Policy Register</h5>
                            </div>

                            {/* Right side - Icons */}
                            <div className="flex gap-3">
                                <div><Mail size={30} /></div>
                                <div><FaWhatsapp size={30} /></div>
                                <div><RiFileExcel2Line size={30} /></div>
                            </div>
                        </div>

                        <div className="row g-3 mb-4 align-items-end">

                            {/* Renewal Period Label - centered over From & To */}
                            <div className="col-lg-6 text-center">
                                <label className="fw-bold">Renewal Period</label>
                            </div>
                            <div className="col-lg-3 text-start">
                                <label className="form-label">Payment Mode</label>
                            </div>
                            <div className="col-lg-3 text-start">
                                <label className="form-label">Product</label>
                            </div>

                            {/* From + To fields side-by-side with inline labels and calendar icons */}
                            <div className="col-lg-3 d-flex align-items-center">
                                <label className="form-label me-2">From</label>
                                <div className="position-relative w-100">
                                    <Field
                                        type="date"
                                        name="renewalFrom"
                                        className="form-control custom-shadow"
                                    />
                                </div>
                            </div>

                            <div className="col-lg-3 d-flex align-items-center">
                                <label className="form-label me-2">To</label>
                                <div className="position-relative w-100">
                                    <Field
                                        type="date"
                                        name="renewalTo"
                                        className="form-control custom-shadow"
                                    />
                                </div>
                            </div>

                            {/* Payment Mode */}
                            <div className="col-lg-3">
                                <Field as="select" name="paymentMode" className="form-select custom-shadow">
                                    <option value="">Select</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Credit Card">Credit Card</option>
                                    <option value="Bank Transfer">Bank Transfer</option>
                                </Field>
                                <ErrorMessage name="paymentMode" component="div" className="text-danger" />
                            </div>

                            {/* Product */}
                            <div className="col-lg-3">
                                <Field as="select" name="productFilter" className="form-select custom-shadow">
                                    <option value="">Select</option>
                                    <option value="Health Suraksha">Health Suraksha</option>
                                    <option value="MediCare">MediCare</option>
                                </Field>
                                <ErrorMessage name="productFilter" component="div" className="text-danger" />
                            </div>
                        </div>

                        {/* Group Selection */}
                        <div className="row g-3 mb-4">
                            <div className="col-lg-12">
                                <label className="form-label">Select Groups</label>
                                <MultiSelectInline
                                    options={groupOptions}
                                    value={selectedGroups}
                                    onChange={setSelectedGroups}
                                    placeholder="Select groups..."
                                />
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <input type="checkbox" name="viewInForcePoly" size={29} /> View In-force Policies only 
                        </div>


                        <div className="flex justify-center p-4">
                            <button type="submit" className="backButton flex justify-center mb-3">Generate</button>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th className="form-label text-truncate" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}></th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} >Policy No</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Group Code</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} >Policy Holder</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} >Product</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} >Sum</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} >Premium</th>
                                        <th className="form-label text-truncate" style={{ maxWidth: "100px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} >Expiry Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map((member, index) => (
                                        <tr key={index}>
                                            <td>  <input type="checkbox" /> </td>
                                            <td>  </td>
                                            <td>  </td>
                                            <td> </td>
                                            <td> </td>
                                            <td> </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default PolicyRegister;